import { useState } from 'react';
import { Gift, TrendingUp, Layers, Check } from 'lucide-react';
import {
  useCompBenefits, useCompVariablePay, useCompBands, useCompStructure,
  useOrgPositions, useOrgDepartments,
} from '../../../hooks';
import { useConfirm } from '../../../components/ui/confirm';
import { useToast } from '../../../components/ui/toast';
import { Modal } from '../../../components/ui/Modal';
import { btnPrimary, btnGhost, thb } from './_shared';
import { leverChips, leverRows } from './_levers';
import {
  BENEFIT_TEMPLATES, VARIABLE_PAY_TEMPLATES, BAND_TABLES, bandRowForLevel,
  type BenefitTemplate, type VariablePayTemplate, type BandTable,
} from './_templates-data';

/**
 * แม่แบบ — ตัวอย่างชุดค่าตอบแทนสำเร็จรูป กดเลือกแล้วใช้ได้ทันที.
 * ทุกแม่แบบเขียนลงตารางจริงผ่าน hook เดิม (ไม่มี migration). Owner แก้ภายหลังได้.
 */

const VP_TYPE: Record<string, string> = { bonus: 'โบนัส', commission: 'คอมมิชชั่น', incentive: 'อินเซนทีฟ', profit_share: 'ส่วนแบ่งกำไร' };

function Card({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col rounded-xl border border-[var(--border)] bg-white p-4">{children}</div>;
}
function Chips({ items }: { items: string[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map((t, i) => (
        <span key={i} className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-xs text-[var(--text-muted)]">{t}</span>
      ))}
    </div>
  );
}
function SectionTitle({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-[var(--brand-600)]">{icon}</span>
      <div>
        <h2 className="text-base font-semibold text-[var(--text)]">{title}</h2>
        <p className="text-sm text-[var(--text-muted)]">{subtitle}</p>
      </div>
    </div>
  );
}

export function TemplatesTab() {
  return (
    <div className="space-y-8">
      <p className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 text-sm text-[var(--text-muted)]">
        เลือกแม่แบบเพื่อสร้างค่าตั้งต้นแบบเร็ว — ระบบจะบันทึกลงแท็บที่เกี่ยวข้องให้ทันที
        แล้วพี่ค่อยเข้าไปปรับตัวเลข/รายละเอียดในแต่ละแท็บได้ตามจริง
      </p>
      <BenefitTemplates />
      <VariablePayTemplates />
      <BandTemplates />
    </div>
  );
}

// ── สวัสดิการ ────────────────────────────────────────────────────────────────
function BenefitTemplates() {
  const benefits = useCompBenefits();
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [viewing, setViewing] = useState<BenefitTemplate | null>(null);

  async function apply(t: BenefitTemplate) {
    if (!(await confirm({ title: `เพิ่มแพ็กเกจ “${t.name}”?`, message: 'จะเพิ่มลงแท็บสวัสดิการ — มอบให้พนักงานได้ภายหลัง', confirmText: 'เพิ่ม' }))) return;
    setBusy(t.id);
    try {
      await benefits.save.mutateAsync({ body: { name: t.name, description: t.description, levers: t.levers } });
      toast.success(`เพิ่ม “${t.name}” แล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เพิ่มไม่สำเร็จ');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="space-y-3">
      <SectionTitle icon={<Gift className="h-5 w-5" />} title="ชุดสวัสดิการสำเร็จรูป" subtitle="เลือกชุดสวัสดิการมาตรฐานแล้วมอบให้พนักงานได้เลย" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {BENEFIT_TEMPLATES.map((t) => (
          <Card key={t.id}>
            <p className="font-semibold text-[var(--text)]">{t.name}</p>
            <p className="mt-1 text-sm text-[var(--text-muted)]">{t.description}</p>
            <Chips items={leverChips(t.levers)} />
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => setViewing(t)} className={btnGhost}>ดู</button>
              <button onClick={() => void apply(t)} disabled={busy === t.id} className={btnPrimary}>
                {busy === t.id ? 'กำลังเพิ่ม…' : '+ เพิ่มลงสวัสดิการ'}
              </button>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.name ?? ''}>
        {viewing && (
          <div className="space-y-4">
            <p className="text-sm text-[var(--text-muted)]">{viewing.description}</p>
            <div className="overflow-hidden rounded-lg border border-[var(--border)]">
              {leverRows(viewing.levers).length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-[var(--text-muted)]">แม่แบบนี้ยังไม่ได้ตั้งค่าสวัสดิการ</p>
              ) : (
                leverRows(viewing.levers).map((r, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2 text-sm last:border-b-0">
                    <span className="text-[var(--text-muted)]">{r.label}</span>
                    <span className="font-medium text-[var(--text)]">{r.value}</span>
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setViewing(null)} className={btnGhost}>ปิด</button>
              <button
                onClick={() => { const t = viewing; setViewing(null); void apply(t); }}
                disabled={busy === viewing.id}
                className={btnPrimary}
              >
                + เพิ่มลงสวัสดิการ
              </button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}

// ── ค่าตอบแทนผันแปร ──────────────────────────────────────────────────────────
function VariablePayTemplates() {
  const vp = useCompVariablePay();
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  async function apply(t: VariablePayTemplate) {
    if (!(await confirm({ title: `เพิ่ม “${t.name}”?`, message: 'จะเพิ่มลงแท็บค่าตอบแทนรวม (ค่าตอบแทนผันแปร)', confirmText: 'เพิ่ม' }))) return;
    setBusy(t.id);
    try {
      await vp.save.mutateAsync({ body: { name: t.name, type: t.type, frequency: t.frequency, formula: t.formula, position_id: null, notes: t.note } });
      toast.success(`เพิ่ม “${t.name}” แล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เพิ่มไม่สำเร็จ');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="space-y-3">
      <SectionTitle icon={<TrendingUp className="h-5 w-5" />} title="โบนัส & ค่าตอบแทนผันแปรสำเร็จรูป" subtitle="แบบของโบนัส/คอมมิชชั่นที่นิยมใช้ — เลือกไว้ก่อน นำไปคำนวณจริงภายหลัง" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {VARIABLE_PAY_TEMPLATES.map((t) => (
          <Card key={t.id}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-[var(--text)]">{t.name}</p>
              <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-xs text-[var(--text-muted)]">{VP_TYPE[t.type]}</span>
            </div>
            <p className="mt-1 text-sm text-[var(--text-muted)]">{t.note}</p>
            <button onClick={() => void apply(t)} disabled={busy === t.id} className={`${btnGhost} mt-3 self-start`}>
              {busy === t.id ? 'กำลังเพิ่ม…' : '+ เพิ่มลงค่าตอบแทนผันแปร'}
            </button>
          </Card>
        ))}
      </div>
    </section>
  );
}

// ── กรอบเงินเดือน & โครงสร้างตามระดับ ──────────────────────────────────────────
function BandTemplates() {
  const positions = useOrgPositions();
  const departments = useOrgDepartments();
  const bands = useCompBands();
  const structure = useCompStructure();
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const posList = positions.data ?? [];
  const existingBandPos = new Set((bands.data ?? []).filter((b) => !b.is_archived).map((b) => b.position_id));
  const existingCells = new Set((structure.data ?? []).map((c) => `${c.department_id}|${c.level}`));

  // เติมกรอบเงินเดือนให้ "ทุกตำแหน่งที่ยังไม่มีกรอบ" ตามระดับของตำแหน่ง
  async function applyBands(table: BandTable) {
    if (posList.length === 0) { toast.error('ยังไม่มีตำแหน่ง — ตั้งใน “โครงสร้างองค์กร” ก่อน'); return; }
    const targets = posList.filter((p) => !existingBandPos.has(p.id));
    if (targets.length === 0) { toast.error('ทุกตำแหน่งมีกรอบเงินเดือนแล้ว'); return; }
    if (!(await confirm({
      title: `เติมกรอบเงินเดือนแบบ “${table.name}”?`,
      message: `จะสร้างกรอบให้ ${targets.length} ตำแหน่งที่ยังไม่มี โดยอิงระดับของแต่ละตำแหน่ง (ตำแหน่งที่มีกรอบแล้วจะไม่ถูกแตะ)`,
      confirmText: `สร้าง ${targets.length} กรอบ`,
    }))) return;
    setBusy(`band:${table.id}`);
    let ok = 0; let fail = 0;
    for (const p of targets) {
      const r = bandRowForLevel(table, p.level);
      try {
        await bands.save.mutateAsync({ body: { position_id: p.id, min_salary: r.min, mid_salary: r.mid, max_salary: r.max, strategy: table.id } });
        ok++;
      } catch { fail++; }
    }
    setBusy(null);
    toast.success(`สร้างกรอบแล้ว ${ok} ตำแหน่ง${fail ? ` · ไม่สำเร็จ ${fail}` : ''}`);
  }

  // เติมตารางโครงสร้าง (แผนก × ระดับ) จาก distinct (department, level) ของตำแหน่งที่มี
  async function applyStructure(table: BandTable) {
    const pairs = new Map<string, { department_id: string; level: number }>();
    for (const p of posList) {
      if (!p.department_id) continue;
      const key = `${p.department_id}|${p.level}`;
      if (!pairs.has(key)) pairs.set(key, { department_id: p.department_id, level: p.level });
    }
    if (pairs.size === 0) { toast.error('ยังไม่มีตำแหน่งที่ผูกแผนก — ตั้งใน “โครงสร้างองค์กร” ก่อน'); return; }
    const targets = [...pairs.values()].filter((c) => !existingCells.has(`${c.department_id}|${c.level}`));
    if (targets.length === 0) { toast.error('ตารางโครงสร้างครบทุกช่องแล้ว'); return; }
    if (!(await confirm({
      title: `เติมตารางโครงสร้างแบบ “${table.name}”?`,
      message: `จะสร้าง ${targets.length} ช่อง (แผนก × ระดับ) ที่ยังว่าง อิงจากตำแหน่งที่มีอยู่ (ช่องที่มีแล้วจะไม่ถูกแตะ)`,
      confirmText: `สร้าง ${targets.length} ช่อง`,
    }))) return;
    setBusy(`struct:${table.id}`);
    let ok = 0; let fail = 0;
    for (const c of targets) {
      const r = bandRowForLevel(table, c.level);
      try {
        await structure.upsert.mutateAsync({ department_id: c.department_id, level: c.level, min_salary: r.min, q2_salary: r.mid, max_salary: r.max });
        ok++;
      } catch { fail++; }
    }
    setBusy(null);
    toast.success(`สร้างช่องแล้ว ${ok}${fail ? ` · ไม่สำเร็จ ${fail}` : ''}`);
  }

  return (
    <section className="space-y-3">
      <SectionTitle icon={<Layers className="h-5 w-5" />} title="กรอบเงินเดือน & โครงสร้างตามระดับ" subtitle="เลือกกลยุทธ์การจ่าย แล้วระบบเติมช่วงเงินเดือนตามระดับให้ทุกตำแหน่ง/แผนกที่ยังว่าง" />
      {posList.length === 0 && (
        <p className="rounded-lg border border-dashed border-[var(--border)] p-4 text-center text-sm text-[var(--text-muted)]">
          ยังไม่มีตำแหน่งในระบบ — ตั้ง “โครงสร้างองค์กร” (แผนก/ตำแหน่ง) ก่อน แล้วกลับมาเติมกรอบจากแม่แบบนี้
        </p>
      )}
      <div className="grid gap-3 lg:grid-cols-3">
        {BAND_TABLES.map((table) => (
          <Card key={table.id}>
            <p className="font-semibold text-[var(--text)]">{table.name}</p>
            <p className="mt-1 text-sm text-[var(--text-muted)]">{table.tagline}</p>
            <div className="mt-3 overflow-hidden rounded-lg border border-[var(--border)]">
              <table className="w-full text-xs">
                <thead className="bg-[var(--surface-2)] text-[var(--text-muted)]">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">ระดับ</th>
                    <th className="px-2 py-1 text-right font-medium">ต่ำสุด</th>
                    <th className="px-2 py-1 text-right font-medium">กลาง</th>
                    <th className="px-2 py-1 text-right font-medium">สูงสุด</th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((r) => (
                    <tr key={r.level} className="border-t border-[var(--border)]">
                      <td className="px-2 py-1 text-[var(--text)]">L{r.level}</td>
                      <td className="px-2 py-1 text-right text-[var(--text-muted)]">{thb(r.min)}</td>
                      <td className="px-2 py-1 text-right text-[var(--text-muted)]">{thb(r.mid)}</td>
                      <td className="px-2 py-1 text-right text-[var(--text-muted)]">{thb(r.max)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <button onClick={() => void applyBands(table)} disabled={!!busy} className={btnPrimary}>
                {busy === `band:${table.id}` ? 'กำลังเติม…' : 'เติมกรอบเงินเดือนรายตำแหน่ง'}
              </button>
              <button onClick={() => void applyStructure(table)} disabled={!!busy} className={btnGhost}>
                {busy === `struct:${table.id}` ? 'กำลังเติม…' : 'เติมตารางโครงสร้าง (แผนก × ระดับ)'}
              </button>
            </div>
          </Card>
        ))}
      </div>
      <p className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
        <Check className="h-3.5 w-3.5" /> ตัวเลขเป็นจุดเริ่มต้นอิงตลาด SME ไทย — เข้าไปปรับในแท็บ “ระดับเงินเดือน” / “โครงสร้างเงินเดือน” ได้ทุกเมื่อ
      </p>
    </section>
  );
}
